import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { Code2, Film, ImageIcon, Newspaper } from 'lucide-react'

import { ChoiceTile } from '@/components/ChoiceTile'
import { CREATIVE_KINDS, type CreativeKind } from '@/services/creatives/bulkUpload'

const KIND_ICON: Record<CreativeKind, ComponentType<{ className?: string }>> = {
  IMAGE: ImageIcon,
  VIDEO: Film,
  THIRD_PARTY: Code2,
  NATIVE: Newspaper,
}

export interface CreativeKindPickerProps {
  value: CreativeKind | null
  onChange: (kind: CreativeKind) => void
  /** Layouts on offer per kind; a kind with none is left out. */
  layoutCounts: Record<CreativeKind, number>
}

/** The first step of a bulk upload: what kind of creatives the files make. */
export function CreativeKindPicker({ value, onChange, layoutCounts }: CreativeKindPickerProps) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {CREATIVE_KINDS.filter((kind) => layoutCounts[kind] > 0).map((kind) => {
        const Icon = KIND_ICON[kind]
        return (
          <ChoiceTile
            key={kind}
            selected={value === kind}
            onSelect={() => onChange(kind)}
            media={<Icon className="size-5 text-primary" />}
            title={t(`creativeUpload.kind.${kind}`)}
            description={
              <>
                {t(`creativeUpload.kindDescription.${kind}`)}
                <span className="mt-1 block text-xs">
                  {t('creativeUpload.layoutCount', { count: layoutCounts[kind] })}
                </span>
              </>
            }
          />
        )
      })}
    </div>
  )
}
